import React from 'react'
import { cn } from './cn'

/**
 * 狀態調性：
 *  - default → 實心墨黑點（完成/就緒）
 *  - live    → 脈動點（處理中/錄音中）
 *  - muted   → 灰點（已排定取消/排隊/已取消）
 *  - danger  → 紅點＋紅字（未授予權限這類失敗狀態；brand-spec「只有失敗上紅」）
 *  - ok      → 完成色的字與圖示（「已允許」這類已經成立的事，design-guideline §2.5）
 *  - warn    → 赭石的字與圖示（需要處理、但系統沒有壞，例如權限還沒允許）
 */
export type StatusTone = 'default' | 'live' | 'muted' | 'danger' | 'ok' | 'warn'

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone
  /** 是否顯示前置狀態點（.dot）。預設 true */
  dot?: boolean
  /** 前置圖示，取代狀態點（設計側 `.st`：圖示＋12px 字）。 */
  icon?: React.ReactNode
  /** 28px 高的髮絲線框（設計側 `.chip`），框色跟著 tone。 */
  outlined?: boolean
}

const toneClass: Record<StatusTone, string> = {
  default: 'bg-foreground',
  live: 'bg-success motion-safe:animate-pulse',
  muted: 'bg-muted',
  danger: 'bg-danger',
  ok: 'bg-ok-deep',
  warn: 'bg-warn',
}

/**
 * `danger` colours the label because it is a failure state; `ok` and `warn`
 * colour it because the words ARE the state (「已允許」「未允許」).
 */
const labelToneClass: Partial<Record<StatusTone, string>> = {
  danger: 'text-danger',
  ok: 'text-ok-deep',
  warn: 'text-warn',
}

const outlineToneClass: Partial<Record<StatusTone, string>> = {
  ok: 'border-[color:var(--ok-line)]',
  warn: 'border-[color:var(--warn-line)]',
}

/**
 * 狀態指示（.status + .dot）：封裝各頁手寫的 `<span className="status"><span className="dot"/>…`。
 * label 由 children 傳入（i18n 在呼叫端），不 hardcode 文案。取代未被使用的舊
 * `StatusIndicator`（decision #3）。同 class、零視覺差。
 */
export function StatusPill({
  tone = 'default',
  dot = true,
  icon,
  outlined = false,
  className,
  children,
  ...rest
}: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-muted',
        labelToneClass[tone],
        outlined &&
          cn(
            'h-7 whitespace-nowrap rounded-[var(--radius-sm)] border px-2.5',
            outlineToneClass[tone] ?? 'border-border'
          ),
        className
      )}
      {...rest}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className="flex size-[1.25em] shrink-0 items-center justify-center [&>svg]:size-full"
        >
          {icon}
        </span>
      ) : dot ? (
        <span className={cn('size-1.5 rounded-full', toneClass[tone])} />
      ) : null}
      {children}
    </span>
  )
}
